import { InteractionMode } from "@/hooks/sticker/useInteractionMode";
import { StickerMode } from "@/hooks/sticker/useStickerMode";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sticker,
  Focus,
  Layers,
  Zap,
  RotateCcw,
  MousePointer,
  Eye,
} from "lucide-react";
import { useTranslation } from "@/lib/i18n";

interface ToolBarProps {
  interactionMode: InteractionMode;
  stickerMode: StickerMode;
  onInteractionModeChange: (mode: InteractionMode) => void;
  onStickerModeChange: (mode: StickerMode) => void;
  onCameraReset: () => void;
}

export const ToolBar = ({
  interactionMode,
  stickerMode,
  onInteractionModeChange,
  onStickerModeChange,
  onCameraReset,
}: ToolBarProps) => {
  const t = useTranslation();

  return (
    <TooltipProvider>
      <Card className="absolute left-2 top-1/2 -translate-y-1/2 md:left-4 z-10 p-1.5 md:p-2 shadow-xl border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-1.5 md:gap-2">
          {/* 交互模式区域 */}
          <div className="flex flex-col gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={interactionMode === "focus" ? "default" : "ghost"}
                  size="sm"
                  className="w-9 h-9 md:w-8 md:h-8 p-0 transition-all duration-200 hover:scale-105 cursor-pointer active:scale-95"
                  onClick={() => onInteractionModeChange("focus")}
                >
                  <Focus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium">
                <p>{t.toolbar.focusModeDesc}</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={interactionMode === "sticker" ? "default" : "ghost"}
                  size="sm"
                  className="w-9 h-9 md:w-8 md:h-8 p-0 transition-all duration-200 hover:scale-105 cursor-pointer active:scale-95"
                  onClick={() => onInteractionModeChange("sticker")}
                >
                  <Sticker className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium">
                <p>{t.toolbar.stickerModeDesc}</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <Separator className="bg-border/60" />

          {/* 渲染模式区域 */}
          <div className="flex flex-col gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={stickerMode === "texture" ? "default" : "ghost"}
                  size="sm"
                  className="w-9 h-9 md:w-8 md:h-8 p-0 transition-all duration-200 hover:scale-105 cursor-pointer active:scale-95"
                  onClick={() => onStickerModeChange("texture")}
                >
                  <Layers className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium">
                <p>
                  {t.toolbar.textureMode} - {t.toolbar.textureModeDesc}
                </p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={stickerMode === "decal" ? "default" : "ghost"}
                  size="sm"
                  className="w-9 h-9 md:w-8 md:h-8 p-0 transition-all duration-200 hover:scale-105 cursor-pointer active:scale-95"
                  onClick={() => onStickerModeChange("decal")}
                >
                  <Zap className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium">
                <p>
                  {t.toolbar.decalMode} - {t.toolbar.decalModeDesc}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          <Separator className="bg-border/60" />

          {/* 视角控制区域 */}
          <div className="flex flex-col gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-9 h-9 md:w-8 md:h-8 p-0 transition-all duration-200 hover:scale-105 cursor-pointer active:scale-95"
                  onClick={onCameraReset}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium">
                <p>{t.toolbar.resetCameraDesc}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </Card>
    </TooltipProvider>
  );
};
